import { useMutation } from '@tanstack/react-query'
import { ApiQueryKeys, SubAccountBillingStatus } from 'globals/constants'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AccountApi, CouponApi } from 'sdk'
import { ProfileModulesliceSelector } from 'store/slices'

interface Props {
  applyCoupon?: boolean
  contactId?: string
}
export const useSubAccountsAndDiscounts = (props: Props) => {
  const FIVE_PERSON_DISCOUNT_CODE = 'sub-account-5'
  const TEN_PERSON_DISCOUNT_CODE = 'sub-account-10'
  const couponApi = new CouponApi()
  const accountApi = new AccountApi()
  const { subAccountAddedCount } = useSelector(ProfileModulesliceSelector)

  const {
    mutate: getCoupon,
    data: coupon,
    isPending: isApplyingCoupon,
  } = useMutation({
    mutationKey: [ApiQueryKeys.payment.FetchCouponById],
    mutationFn: (code: string) => couponApi.ValidateCoupon(code),
  })

  const {
    data: allSubAccounts,
    isPending: isGettingSubAccounts,
    mutate: getSubAccounts,
  } = useMutation({
    mutationKey: [ApiQueryKeys.account.GetUserAllSubAccounts],
    mutationFn: () => accountApi.GetUserAllSubAccounts(props.contactId),
    onSuccess: (data) => {
      if (props.applyCoupon) {
        const billable = data.filter((x) => x.BillingStatus === SubAccountBillingStatus.Billable)
        const totalSeats = (billable.length ?? 0) + 1
        if (totalSeats) {
          if (totalSeats >= 5 && totalSeats < 10) {
            getCoupon(FIVE_PERSON_DISCOUNT_CODE)
          } else if (totalSeats >= 10) {
            getCoupon(TEN_PERSON_DISCOUNT_CODE)
          }
        }
      }
    },
  })
  const { mutate: resendInvitation, isPending: isInviting } = useMutation({
    mutationKey: [ApiQueryKeys.account.resendInivitaion],
    mutationFn: (subAccountId: string) => accountApi.resendSubAccountInvitation(subAccountId),
    onSuccess: (data) => enqueueSnackbar('Invitation sent successfuly', { variant: 'success' }),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  })
  const billableSubAccounts = useMemo(() => {
    return allSubAccounts?.filter(
      (x) =>
        x.BillingStatus === SubAccountBillingStatus.Billable ||
        x.BillingStatus === SubAccountBillingStatus.Soon_To_Be_Removed
    )
  }, [allSubAccounts])

  const activeBillingSubAccounts = useMemo(() => {
    return allSubAccounts?.filter((x) => x.BillingStatus === SubAccountBillingStatus.Billable)
  }, [allSubAccounts])

  useEffect(() => {
    getSubAccounts()
  }, [subAccountAddedCount])

  return {
    getSubAccounts,
    resendInvitation,
    isInviting,
    allSubAccounts,
    billableSubAccounts,
    isGettingAccounts: isGettingSubAccounts,
    isGettingCoupons: isApplyingCoupon,
    activeBillingSubAccounts,
    coupon,
  }
}
